import { CurecallSearchableSelect } from '@honestica/curecall-ui'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useSession } from 'src/providers/auth.provider'
import { getStringWithFirstLetterInUpperCase } from 'src/utils/string-formatter.util'
import { useTranslation } from 'react-i18next'
import { TMedicalStaffMember } from '../../types/medical-staff-member'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { LocalizedLink } from '../navigation/localized-link.component'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'

interface Props {
  name: string
  projectId: number
  isRequired?: boolean
  label?: string
  placeholder?: string
  referringDoctor?: TMedicalStaffMember
  customErrorMessage?: string
  labelClassName?: string
  withDefaultValue?: boolean
}

export interface IUserOption {
  label: string
  value: number | undefined | null
}

const SelectMedicalStaffMember = ({
  name,
  projectId,
  isRequired,
  label,
  placeholder,
  referringDoctor,
  customErrorMessage,
  labelClassName,
  withDefaultValue,
}: Props) => {
  const methods = useFormContext()
  const { currentUser } = useSession()
  const { activeDoctors, isFeatureActivated } = useCurrentProject()
  const { t } = useTranslation()

  const {
    formState: { errors },
    setValue,
    getValues,
    control,
    clearErrors,
  } = methods
  const [medicalStaffMember, setMedicalStaffMember] = useState<IUserOption | null>(null)
  const errorMessage =
    customErrorMessage || t('components.select-medical-staff-member.please-select-referring-doctor')

  const emptyOption: IUserOption = {
    label: t('components.select-medical-staff-member.empty-field'),
    value: null,
  }
  const sortedMedicalStaffMembers = activeDoctors.sort((a, b) => {
    const nameA = a.lastname + ' ' + a.firstname
    const nameB = b.lastname + ' ' + b.firstname
    return nameA.localeCompare(nameB)
  })
  const medicalStaffMembersOptions: IUserOption[] = useMemo(
    () =>
      sortedMedicalStaffMembers.map((medicalStaffMembers) => ({
        label: `${medicalStaffMembers.lastname?.toUpperCase()}, ${getStringWithFirstLetterInUpperCase(
          medicalStaffMembers.firstname || '',
        )}`,
        value: medicalStaffMembers.id,
      })),
    [sortedMedicalStaffMembers],
  )

  useEffect(() => {
    const alreadySavedId = getValues(name)
    if (alreadySavedId) {
      const savedMedicalStaffMember = medicalStaffMembersOptions.find(
        (option) => option.value === alreadySavedId,
      )
      savedMedicalStaffMember && setMedicalStaffMember(savedMedicalStaffMember)
      return
    }
    if (alreadySavedId === null) {
      setMedicalStaffMember(emptyOption)
      return
    }

    if (withDefaultValue === false) return
    if (medicalStaffMembersOptions?.length === 1 && medicalStaffMembersOptions[0]) {
      handleChangeUser(medicalStaffMembersOptions[0])
      return
    }

    const referringPractitionner =
      referringDoctor?.id && medicalStaffMembersOptions.find((option) => option.value === referringDoctor.id)
    if (referringPractitionner) {
      handleChangeUser(referringPractitionner)
      return
    }

    const currentUserAsMedicalStaffMember =
      currentUser?.id && medicalStaffMembersOptions.find((option) => option.value === currentUser.id)
    if (currentUserAsMedicalStaffMember) {
      handleChangeUser(currentUserAsMedicalStaffMember)
    }
  }, [medicalStaffMembersOptions?.length, referringDoctor?.id]) //eslint-disable-line

  const handleChangeUser = async (medicalStaffMember: IUserOption) => {
    setMedicalStaffMember(medicalStaffMember)
    setValue && setValue(name, medicalStaffMember.value)
    medicalStaffMember && clearErrors(name)
  }

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={{ required: isRequired }}
        render={() => (
          <CurecallSearchableSelect
            label={label || t('components.select-medical-staff-member.medical-staff-member-name')}
            items={isRequired ? medicalStaffMembersOptions : [emptyOption, ...medicalStaffMembersOptions]}
            onSelect={handleChangeUser}
            defaultItemSelected={medicalStaffMember}
            placeholder={
              placeholder || t('components.select-medical-staff-member.select-a-medical-staff-member')
            }
            isRequired={isRequired}
            errorMessage={errors[name] && sortedMedicalStaffMembers.length > 0 ? errorMessage : ''}
            labelClassName={labelClassName}
          />
        )}
      />

      {medicalStaffMembersOptions.length === 0 && (
        <div className="c-relative">
          <div className="c-absolute c-top-1">
            <p className="c-m-0 c-font-nexa c-text-sm c-text-danger-500 xl:c-text-base xl:c-tracking-tight 2xl:c-text-lg 2xl:c-tracking-tightest">
              {t('components.select-medical-staff-member.no-doctor-in-project')}
            </p>
            {currentUser?.isAdmin && isFeatureActivated('is-configuration-activated') ? (
              <LocalizedLink
                to={`/${projectId}/configuration/medical-staff-members`}
                className="c-text-primary-600 c-text-sm c-font-nexa xl:c-text-base xl:c-tracking-tight 2xl:c-text-lg 2xl:c-tracking-tightest c-flex c-items-center c-gap-1"
              >
                <AdminOnlyBadge />
                {t('components.select-medical-staff-member.click-here-to-add-one')}
              </LocalizedLink>
            ) : (
              <p className="c-m-0 c-font-nexa c-text-sm c-text-danger-500 xl:c-text-base xl:c-tracking-tight 2xl:c-text-lg 2xl:c-tracking-tightest">
                {t('components.select-medical-staff-member.contact-curecall')}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default SelectMedicalStaffMember
